class SenfonStoryHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.senfonHeroReady === 'true') return;

    this.slides = Array.from(this.querySelectorAll('[data-senfon-slide]'));
    this.paginationButtons = Array.from(this.querySelectorAll('[data-senfon-slide-to]'));
    this.previousButton = this.querySelector('[data-senfon-previous]');
    this.nextButton = this.querySelector('[data-senfon-next]');
    this.autoplayToggle = this.querySelector('[data-senfon-autoplay-toggle]');
    this.activeIndex = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mobile = window.matchMedia('(max-width: 749px)').matches;
    this.autoplayEnabled = this.mobile
      ? this.dataset.mobileAutoplay === 'true'
      : this.dataset.desktopAutoplay === 'true';
    this.autoplay = this.autoplayEnabled && !this.reducedMotion;
    this.userPaused = false;
    this.focusPaused = false;
    this.intervalMs = Math.max(3000, Number(this.dataset.interval || 5000));
    this.handleVisibilityChange = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };
    this.handleWindowLoad = () => this.startAutoplay();

    if (this.slides.length < 2) {
      if (!this.mobile && !this.reducedMotion) this.queueVideo(0);
      this.dataset.senfonHeroReady = 'true';
      return;
    }

    this.paginationButtons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(Number(button.dataset.senfonSlideTo), true));
    });

    this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1, true));
    this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1, true));
    this.autoplayToggle?.addEventListener('click', () => this.toggleAutoplay());
    if (this.autoplayToggle) this.autoplayToggle.hidden = !this.autoplay;
    this.updateAutoplayControl();

    this.addEventListener('focusin', () => {
      this.focusPaused = true;
      this.stopAutoplay();
    });
    this.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.focusPaused = false;
          this.startAutoplay();
        }
      }, 0);
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (!this.mobile && !this.reducedMotion) this.queueVideo(0);
    this.queueNextSlideImage();
    if (this.mobile && document.readyState !== 'complete') {
      window.addEventListener('load', this.handleWindowLoad, { once: true });
    } else {
      this.startAutoplay();
    }
    this.dataset.senfonHeroReady = 'true';
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('load', this.handleWindowLoad);
  }

  goTo(index, userInitiated = false) {
    const total = this.slides.length;
    const nextIndex = (index + total) % total;
    if (nextIndex === this.activeIndex && !userInitiated) return;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', String(!isActive));
      const video = slide.querySelector('video[data-senfon-video]');
      if (!isActive && video) video.pause();
    });

    this.paginationButtons.forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', String(buttonIndex === nextIndex));
      button.setAttribute('aria-pressed', String(buttonIndex === nextIndex));
    });

    this.activeIndex = nextIndex;
    if (!this.mobile && !this.reducedMotion) this.queueVideo(nextIndex);
    this.queueNextSlideImage();

    if (userInitiated) {
      this.stopAutoplay();
      if (!this.userPaused) this.startAutoplay();
    }
  }

  queueNextSlideImage() {
    if (this.slides.length < 2) return;
    const nextIndex = (this.activeIndex + 1) % this.slides.length;
    if (nextIndex === 0) return;
    const nextPicture = this.slides[nextIndex]?.querySelector('picture');
    if (!nextPicture || nextPicture.dataset.prepared === 'true') return;

    const prepare = () => {
      if (nextPicture.dataset.prepared === 'true') return;
      const image = nextPicture.querySelector('img');
      if (!image) return;
      nextPicture.dataset.prepared = 'true';
      image.loading = 'eager';
      image.fetchPriority = 'low';
    };

    const prepareDelay = this.mobile ? 1200 : 600;
    if (document.readyState === 'complete') {
      window.setTimeout(prepare, prepareDelay);
    } else {
      window.addEventListener('load', () => window.setTimeout(prepare, prepareDelay), { once: true });
    }
  }

  queueVideo(index) {
    const slide = this.slides[index];
    const video = slide?.querySelector('video[data-senfon-video]');
    if (!video || video.dataset.loaded === 'true') return;

    const loadVideo = () => {
      if (video.dataset.loaded === 'true') return;
      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      if (!sources.length) return;

      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.addEventListener(
        'canplay',
        () => {
          video.classList.add('is-ready');
          video.play().catch(() => {});
        },
        { once: true }
      );
      video.load();
    };

    if (index === 0 && 'requestIdleCallback' in window) {
      window.requestIdleCallback(loadVideo, { timeout: 1800 });
    } else if (index === 0) {
      window.setTimeout(loadVideo, 800);
    } else {
      loadVideo();
    }
  }

  updateAutoplayControl() {
    if (!this.autoplayToggle) return;
    const paused = this.userPaused || !this.autoplay;
    this.autoplayToggle.setAttribute('aria-pressed', String(paused));
    this.autoplayToggle.setAttribute('aria-label', paused ? 'Play slides' : 'Pause slides');
    const label = this.autoplayToggle.querySelector('[data-senfon-autoplay-label]');
    if (label) label.textContent = paused ? 'Play' : 'Pause';
  }

  toggleAutoplay() {
    if (!this.autoplay) return;
    this.userPaused = !this.userPaused;
    if (this.userPaused) this.stopAutoplay();
    else this.startAutoplay();
    this.updateAutoplayControl();
  }

  resetProgress() {
    if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
    this.progressFrame = undefined;
    this.classList.remove('is-progressing');
  }

  startAutoplay() {
    if (this.mobile && document.readyState !== 'complete') return;
    if (!this.autoplay || this.userPaused || this.focusPaused || this.autoplayTimer || document.hidden) return;
    this.resetProgress();
    this.style.setProperty('--senfon-story-progress-duration', `${this.intervalMs}ms`);
    this.progressFrame = window.requestAnimationFrame(() => {
      this.classList.add('is-progressing');
      this.progressFrame = undefined;
      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = undefined;
        this.goTo(this.activeIndex + 1);
        this.startAutoplay();
      }, this.intervalMs);
    });
  }

  stopAutoplay() {
    if (this.autoplayTimer) window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = undefined;
    this.resetProgress();
  }
}

if (!customElements.get('senfon-story-hero')) {
  customElements.define('senfon-story-hero', SenfonStoryHero);
}
